/** @graphql agg **/
import { useEffect } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { teamToTemplatesMapping } from '../localMappings/teamToTemplatesMapping.ts';

import type {
	useUserTeamTypeQuery$data,
	useUserTeamTypeQuery$variables,
} from './__generated__/useUserTeamTypeQuery.graphql.ts';

const VALID_TEAM_TYPES = Object.keys(teamToTemplatesMapping);

const userTeamTypeQuery = graphql`
	query useUserTeamTypeQuery($userId: ID!) {
		user(accountId: $userId) {
			__typename
			... on AtlassianAccountUser {
				extendedProfile {
					teamType
				}
			}
		}
	}
`;

const sanitizeTeamType = (teamType: string | undefined | null): string | undefined => {
	if (teamType && VALID_TEAM_TYPES.includes(teamType)) {
		return teamType;
	}
	return undefined;
};

export interface UseUserTeamTypeResult {
	teamType: string | undefined;
	loading: boolean;
	error?: Error;
}

/**
 * Fetches the user's team type from their Atlassian extended profile via AGG.
 * Only returns team types that are valid for the template touchpoint experiment
 * (i.e. present in teamToTemplatesMapping).
 */
export const useUserTeamType = ({ skip }: { skip?: boolean } = {}): UseUserTeamTypeResult => {
	const { userId } = useSessionData();
	const isLicensed = useIsLicensed();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { data, loading, error } = useQueryInterop<
		useUserTeamTypeQuery$data,
		useUserTeamTypeQuery$variables
	>(userTeamTypeQuery, {
		variables: { userId: userId ?? '' },
		skip: skip || !isLicensed || !userId,
		interopApolloAggClient: true,
	});

	useEffect(() => {
		if (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'useUserTeamTypeQuery',
					source: 'useUserTeamType',
					attributes: {
						message: error.message,
					},
				},
			}).fire();
		}
	}, [error, createAnalyticsEvent]);

	if (loading) {
		return { teamType: undefined, loading: true };
	}

	if (error) {
		return { teamType: undefined, loading: false, error };
	}

	if (data?.user?.__typename === 'AtlassianAccountUser') {
		return {
			teamType: sanitizeTeamType(data.user.extendedProfile?.teamType?.toLocaleUpperCase()),
			loading: false,
		};
	}

	return { teamType: undefined, loading: false };
};
